import React, { Suspense, lazy, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { setCanonical, clearPageJsonLd } from './seo';
import App from './App';
import LaunchBanner from './LaunchBanner';
import ErrorBoundary from './ErrorBoundary';
import { COLORS } from './SharedUI';

// Widgets non-critiques (overlays flottants) : code-splittés hors du bundle
// principal ET montés seulement après le 1er paint (requestIdleCallback) pour
// alléger le chemin critique (TBT/INP mobile). Aucun n'affiche de contenu au
// 1er rendu (null tant qu'un timer/scroll/trigger n'a pas eu lieu), donc les
// différer ne crée aucun décalage d'hydratation.
const LiveChat = lazy(() => import('./LiveChat'));
const CookieBanner = lazy(() => import('./CookieBanner'));
const ExitIntent = lazy(() => import('./ExitIntent'));
const StickyCTAMobile = lazy(() => import('./StickyCTAMobile'));
const BackToTop = lazy(() => import('./SharedUI').then(m => ({ default: m.BackToTop })));

// Lazy load des pages secondaires (chargées à la demande)
const Valorisation = lazy(() => import('./pages/Valorisation'));
const Previsionnel = lazy(() => import('./pages/Previsionnel'));
const BusinessPlan = lazy(() => import('./pages/BusinessPlan'));
const AuditTresorerie = lazy(() => import('./pages/AuditTresorerie'));
const Cession = lazy(() => import('./pages/Cession'));
const TableauBord = lazy(() => import('./pages/TableauBord'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DashboardAdmin = lazy(() => import('./pages/DashboardAdmin'));
const Merci = lazy(() => import('./pages/Merci'));
const TestTracking = lazy(() => import('./pages/TestTracking'));
const APropos = lazy(() => import('./pages/APropos'));
const Glossaire = lazy(() => import('./pages/Glossaire'));
const Tarifs = lazy(() => import('./pages/Tarifs'));
const Contact = lazy(() => import('./pages/Contact'));
const FAQ = lazy(() => import('./pages/FAQ'));
const NotFound = lazy(() => import('./pages/NotFound'));
const MentionsLegales = lazy(() => import('./pages/MentionsLegales'));
const CGV = lazy(() => import('./pages/CGV'));
const Confidentialite = lazy(() => import('./pages/Confidentialite'));
const SectorRestaurant = lazy(() => import('./pages/SectorRestaurant'));
const SectorBTP = lazy(() => import('./pages/SectorBTP'));
const SectorSaaS = lazy(() => import('./pages/SectorSaaS'));
const SectorCommerce = lazy(() => import('./pages/SectorCommerce'));
const SectorIndustrie = lazy(() => import('./pages/SectorIndustrie'));
const SectorSante = lazy(() => import('./pages/SectorSante'));
const SectorImmobilier = lazy(() => import('./pages/SectorImmobilier'));
const SitemapPage = lazy(() => import('./pages/SitemapPage'));
const GuideGratuit = lazy(() => import('./pages/GuideGratuit'));
const DiagnosticGratuit = lazy(() => import('./pages/DiagnosticGratuit'));
const ValorisationGratuite = lazy(() => import('./pages/ValorisationGratuite'));
const AuditTresorerieGratuit = lazy(() => import('./pages/AuditTresorerieGratuit'));
const PrevisionnelGratuit = lazy(() => import('./pages/PrevisionnelGratuit'));
const Methodologie = lazy(() => import('./pages/Methodologie'));
const Processus = lazy(() => import('./pages/Processus'));
const ValorisationCity = lazy(() => import('./pages/ValorisationCity'));
const AdminEmails = lazy(() => import('./pages/AdminEmails'));
const AvisClients = lazy(() => import('./pages/AvisClients'));
const CombienVaut = lazy(() => import('./pages/CombienVaut'));
const CombienVautIndex = lazy(() => import('./pages/CombienVautIndex'));
const CalcEBITDA = lazy(() => import('./pages/CalcEBITDA'));
const CalcBFR = lazy(() => import('./pages/CalcBFR'));
const CalcDSCR = lazy(() => import('./pages/CalcDSCR'));
const CalcCapaciteEmprunt = lazy(() => import('./pages/CalcCapaciteEmprunt'));
const CalcPacteDutreil = lazy(() => import('./pages/CalcPacteDutreil'));
const CalcFondsCommerce = lazy(() => import('./pages/CalcFondsCommerce'));
const BlogIndex = lazy(() => import('./pages/BlogIndex'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const CasPratiques = lazy(() => import('./pages/CasPratiques'));
const Quiz = lazy(() => import('./pages/Quiz'));

// Skeleton loader élégant pendant le chargement
function PageLoader() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F4EFE4' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: `conic-gradient(${COLORS.blue}, ${COLORS.green}, ${COLORS.blue})`, margin: '0 auto 16px', animation: 'rotate-slow 1.2s linear infinite', maskImage: 'radial-gradient(circle, transparent 50%, black 53%)', WebkitMaskImage: 'radial-gradient(circle, transparent 50%, black 53%)' }} />
        <div style={{ fontSize: '13px', color: '#888', fontWeight: '500' }}>Chargement…</div>
      </div>
      <style>{`@keyframes rotate-slow { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// Service Worker pour PWA offline-first
if ('serviceWorker' in navigator && window.location.hostname !== 'localhost') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch(() => {});
  });
}

/**
 * CanonicalSync — met à jour <link rel="canonical"> = URL réelle à chaque
 * changement de route. Corrige le bug SPA où le canonical pointait vers "/"
 * sur toutes les pages (Google les voyait comme des doublons de l'accueil).
 * Couvre 100% des routes, présentes et futures, sans toucher chaque page.
 */
function CanonicalSync() {
  const location = useLocation();
  useEffect(() => {
    setCanonical(location.pathname);
  }, [location.pathname]);
  return null;
}

/**
 * DeferUntilIdle — ne monte ses enfants qu'APRÈS le 1er paint, quand le
 * navigateur est inactif (requestIdleCallback). Rend `null` pendant le
 * pré-rendu Puppeteer (navigator.webdriver) → le HTML statique ne contient
 * pas ces widgets, et l'hydratation démarre donc aussi sur `null` (pas de
 * mismatch). Sert à sortir les overlays non-critiques du chemin critique.
 */
function DeferUntilIdle({ children }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (window.navigator.webdriver) return; // pré-rendu : ne jamais monter
    const ric = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 200));
    const cancel = window.cancelIdleCallback || window.clearTimeout;
    const id = ric(() => setReady(true));
    return () => cancel(id);
  }, []);
  return ready ? children : null;
}

const rootElement = document.getElementById('root');

// Purge des blocs JSON-LD de page hérités du HTML statique.
//
// Le pré-rendu écrit build/index.html à partir de l'accueil ; ce fichier sert
// ensuite de point de départ à TOUTES les autres routes. Sans cette purge, le
// balisage de l'accueil (ProfessionalService, FAQPage, HowTo) se retrouvait
// recopié sur /blog, /faq, /valorisation… — exactement le défaut que la mise en
// place du balisage par page devait corriger (audit 2026-08-15).
// Chaque page repose ensuite le sien via setPageJsonLd.
clearPageJsonLd();

const AppTree = (
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <CanonicalSync />
        <a href="#main-content" style={{ position: 'absolute', left: '-9999px', top: 0, padding: '8px 16px', background: COLORS.blue, color: 'white', zIndex: 99999, textDecoration: 'none', borderRadius: '0 0 8px 0' }}
          onFocus={e => e.currentTarget.style.left = '0'}
          onBlur={e => e.currentTarget.style.left = '-9999px'}>
          Aller au contenu principal
        </a>
        <LaunchBanner />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<App />} />

            <Route path="/valorisation" element={<Valorisation />} />
            <Route path="/previsionnel" element={<Previsionnel />} />
            <Route path="/business-plan" element={<BusinessPlan />} />
            <Route path="/audit-tresorerie" element={<AuditTresorerie />} />
            <Route path="/cession" element={<Cession />} />
            <Route path="/tableau-de-bord-mensuel" element={<TableauBord />} />
            <Route path="/tableau-de-bord" element={<TableauBord />} />
            <Route path="/dashboard/:token" element={<Dashboard />} />
            <Route path="/admin/dashboard" element={<DashboardAdmin />} />
            <Route path="/merci" element={<Merci />} />
            <Route path="/test-tracking" element={<TestTracking />} />

            <Route path="/valorisation/restaurant" element={<SectorRestaurant />} />
            <Route path="/valorisation/btp" element={<SectorBTP />} />
            <Route path="/valorisation/saas" element={<SectorSaaS />} />
            <Route path="/valorisation/commerce" element={<SectorCommerce />} />
            <Route path="/valorisation/industrie" element={<SectorIndustrie />} />
            <Route path="/valorisation/sante" element={<SectorSante />} />
            <Route path="/valorisation/immobilier" element={<SectorImmobilier />} />
            <Route path="/sitemap" element={<SitemapPage />} />
            <Route path="/guide-gratuit" element={<GuideGratuit />} />
            <Route path="/diagnostic-gratuit" element={<DiagnosticGratuit />} />
            <Route path="/valorisation-gratuite" element={<ValorisationGratuite />} />
            <Route path="/audit-tresorerie-gratuit" element={<AuditTresorerieGratuit />} />
            <Route path="/previsionnel-gratuit" element={<PrevisionnelGratuit />} />
            <Route path="/methodologie" element={<Methodologie />} />
            <Route path="/processus" element={<Processus />} />
            <Route path="/valorisation/ville/:slug" element={<ValorisationCity />} />
            <Route path="/admin/emails" element={<AdminEmails />} />
            <Route path="/avis-clients" element={<AvisClients />} />
            <Route path="/combien-vaut" element={<CombienVautIndex />} />
            <Route path="/combien-vaut/:slug" element={<CombienVaut />} />

            <Route path="/outils/calcul-ebitda" element={<CalcEBITDA />} />
            <Route path="/outils/calcul-bfr" element={<CalcBFR />} />
            <Route path="/outils/calcul-dscr" element={<CalcDSCR />} />
            <Route path="/outils/capacite-emprunt" element={<CalcCapaciteEmprunt />} />
            <Route path="/outils/pacte-dutreil" element={<CalcPacteDutreil />} />
            <Route path="/outils/fonds-de-commerce" element={<CalcFondsCommerce />} />

            <Route path="/a-propos" element={<APropos />} />
            <Route path="/glossaire" element={<Glossaire />} />
            <Route path="/tarifs" element={<Tarifs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            <Route path="/etudes-de-cas" element={<CasPratiques />} />
            <Route path="/quiz" element={<Quiz />} />

            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/cgv" element={<CGV />} />
            <Route path="/confidentialite" element={<Confidentialite />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <Suspense fallback={null}>
          <DeferUntilIdle>
            <LiveChat />
            <BackToTop />
            <CookieBanner />
            <ExitIntent />
            <StickyCTAMobile />
          </DeferUntilIdle>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);

// Rendu client, y compris quand le HTML a été pré-rendu.
//
// Pourquoi PAS hydrateRoot (audit 2026-08-15) : notre pré-rendu (prerender.js)
// sérialise le DOM d'un vrai navigateur via page.content(). Cette sérialisation
// ne peut pas produire les marqueurs de frontière Suspense (`<!--$-->` /
// `<!--/$-->`) que hydrateRoot attend — seul renderToString/renderToPipeableStream
// les émet. Vérifié : 0 commentaire dans le #root pré-rendu. Or l'arbre contient
// deux <Suspense> (routes lazy + widgets différés), donc l'hydratation échouait
// SYSTÉMATIQUEMENT : React #418 puis #423 (« the entire root will switch to
// client rendering »), 7 erreurs par chargement en production, et l'arbre entier
// reconstruit après une tentative d'hydratation inutile.
//
// createRoot fait donc explicitement ce que React finissait par faire de force,
// mais sans la tentative ratée ni les erreurs console. Le SEO n'est pas touché :
// le HTML pré-rendu reste servi tel quel et c'est lui que lisent les crawlers.
// Pour récupérer un vrai bénéfice d'hydratation, il faudrait passer le pré-rendu
// à renderToString (chantier séparé) — et alors seulement remettre hydrateRoot.
createRoot(rootElement).render(AppTree);
